import { isStaffShopSession } from "./shopSession.ts";
import { readOrders, readOrder, saveOrder, hideOrder, searchVariants } from "./storeCommerceService.ts";

const SHOP_PERSON_KEY = "shopSelectedPersonId";

/** Staff: person chosen in shop UI. */
export const getStaffPersonId = (): number | undefined => {
  const raw = localStorage.getItem(SHOP_PERSON_KEY);
  const personId = raw ? Number(raw) : undefined;
  return personId && personId > 0 ? personId : undefined;
};

const normalizePersonId = (personId: unknown): number | undefined => {
  const id = typeof personId === "number" ? personId : Number(personId);
  return id > 0 && Number.isFinite(id) ? id : undefined;
};

/** Persist staff's selected person without changing orders. */
export const persistStaffPersonId = (personId: unknown) => {
  const normalizedId = normalizePersonId(personId);
  if (!normalizedId) return;
  localStorage.setItem(SHOP_PERSON_KEY, String(normalizedId));
};

/** Staff orders/detail pages: select a person without changing orders. */
export const selectStaffPersonForOrders = (personId: unknown) => {
  persistStaffPersonId(personId);
  notifyStaffPersonUpdated();
};

export const clearStaffShopSession = () => {
  localStorage.removeItem(SHOP_PERSON_KEY);
};

export const clearStaffPersonSelection = () => {
  localStorage.removeItem(SHOP_PERSON_KEY);
  notifyStaffPersonUpdated();
};

/** Shop ownership is resolved exclusively from the authenticated JWT by the API. */
export const getRequestPersonId = (): undefined => {
  return undefined;
};

export const hasStaffPersonSelected = () =>
  isStaffShopSession() && Boolean(getStaffPersonId());

export const getOrders = async (personId?: number) => {
  return readOrders();
};

export const getOrderById = async (id: number, personId?: number, preserveVersion = false) => {
  return readOrder(id, preserveVersion);
};

export const searchOrderPatterns = async (searchTerm: string) => {
  return searchVariants(searchTerm || "");
};

export const deleteOrder = async (
  orderId: number,
  onSuccess?: () => void
) => {
  await hideOrder(orderId);
  onSuccess?.();
};

export const updateShopOrder = async (
  orderId: number,
  body: Record<string, unknown>,
  onSuccess?: () => void
) => {
  await saveOrder(orderId, body);
  onSuccess?.();
};

export const notifyStaffPersonUpdated = () => {
  window.dispatchEvent(new Event("staffPersonUpdated"));
};
